import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'
import { Heading, Stack, Flex, Text } from "@chakra-ui/react"
import { LuHouse } from "react-icons/lu"
import { useForm, useWatch } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { InputBox, SelectBox } from '../../components/shared/fields'
import ButtonBox from '../../components/shared/buttons/ButtonBox'
import { GridCol, GridRow } from '../../components/shared/grids'
import BreadCrumbBox from '../../components/shared/BreadCrumbBox'
import { useColorModeValue } from '../../components/ui/color-mode'
import DialogBox from '../../components/shared/dialogs/DialogBox'
import { dateToTimeStamp, complaint_priorities_list } from '../../utils/constants'
import { darkMode, lightMode } from '../../theme/config'
import styles from './complaintadd.module.css';
import { closeDialog, openDialog } from '../../store/dialogSlice';
import ImageUploader from '../../components/shared/ImageUploader';
import { listPath, uploadPath } from '../../utils/constants/insightsPath';
import { resetListData } from '../../store/listSlice';
import { clearComplaintDetail } from '../../store/complaintSlice';

const ComplaintAdd = () => {

  const borderColor = useColorModeValue(lightMode.containerBorderColor, darkMode.containerBorderColor);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cancelFormModalOpen = useSelector((state) => state.dialogs.addComplaint);
  const userId = useSelector(state => state.user.userId);
  const id = useSelector(state => state.complaint.id);
  const currentProductId = useSelector(state => state.user.currentProductId);
  const { firstName, lastName, email, phone } = useSelector(state => state.user);

  const imageFiles = useSelector(state => state.files[uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD] || []);
  const [imageError, setImageError] = React.useState(null);

  const complaintData = useSelector((state) => state.complaint || {
    title: '',
    isEdit: false,
    productId: '',
    customerName: '',
    email: '',
    contactNumber: '',
    categoryObject: {},
    subCategoryObject: {},
    priorityObject: {},
    complaintTitle: '',
    complaintDescription: '',
    complaintReferenceNumber: '',
    complaintId: '',
  });

  // console.log(complaintData);

  const { serviceRequestCategories, serviceRequestSubcategories } = useSelector((state) => state.categories || {
    serviceRequestCategories: [],
    serviceRequestSubcategories: {}
  })

  const customer = useSelector((state) => state.user);

  const redStar = <span style={{ color: "red" }}>*</span>;
  // Error Handling using zod and react hook form
  const schema = z.object({
    complaintType: z.array(z.string(), { required_error: "Complaint Type is required" }).nonempty("Complaint Type is required"),
    complaintSubCategory: z.array(z.string(), { required_error: "Complaint Sub Category is required" }).nonempty("Complaint Sub Category is required"),
    complaintPriority: z.array(z.string(), { required_error: "Complaint Priority is required" }).nonempty("Complaint Priority is required"),
    description: z.string().nonempty("Description is required").optional(),
    title: z.string().nonempty("Title is required"),
    complaintReferenceNo: z.string().optional(),

  })
  const { register, handleSubmit, control, formState: { errors, isDirty }, setValue } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      complaintType: complaintData.categoryObject?.value ? [complaintData.categoryObject.value] : [],
      complaintSubCategory: complaintData.subCategoryObject?.value ? [complaintData.subCategoryObject.value] : [],
      complaintPriority: complaintData.priorityObject?.value ? [complaintData.priorityObject.value] : [],
    }
  });

  const useResetOnChange = (control, watchField, resetField, setValue) => {
    const watchedValue = useWatch({ control, name: watchField });
    React.useEffect(() => {
      setValue(resetField, []);
    }, [watchedValue, setValue]);
    return watchedValue;
  };

  const selectedComplaintCategory = useResetOnChange(control, 'complaintType', 'complaintSubCategory', setValue);


  // User go back from page after clicking cancel
  const goBackToHomePage = () => {
    if (complaintData.isEdit && !complaintData.customerId) {
      navigate('/complaint/index');
    } else {
      navigate('/customers/index');
    }
  }

  // Redirect to home page on page refresh
  useEffect(() => {
    if (!customer.userId && !complaintData.isEdit) {
      goBackToHomePage();
    }
  }, []);

  const handleFormCancel = () => {
    if (!isDirty) {
      dispatch(clearComplaintDetail());
      goBackToHomePage();
    } else {
      dispatch(openDialog('addComplaint'));
    }
  };

  const handleCancelClose = () => dispatch(closeDialog('addComplaint'));

  const breadCrumbList = [{ id: '1', icon: <LuHouse />, pageName: 'Complaint', onClick: handleFormCancel }]

  // Submit the data to api
  const onSubmit = handleSubmit(async (data) => {

    if (!complaintData.isEdit && (imageFiles.files?.length === 0 || imageFiles?.length === 0)) {
      setImageError('Image is required.');
      return;
    }
    setImageError('');

    const inputData = {
      customerId: complaintData.customerId || userId,
      customerName: complaintData.customerName || `${firstName ?? ''} ${lastName ?? ''}`.trim(),
      email: complaintData.email || email,
      contactNumber: complaintData.contactNumber || phone,
      description: data.description ?? '',
      createdDate: dateToTimeStamp(),
      lastUpdatedDate: dateToTimeStamp(),
      complaintCategory: data.complaintType[0],
      complaintSubcategory: data.complaintSubCategory[0],
      productId: complaintData.productId || currentProductId,
      title: data.title,
      customMetadata: JSON.stringify({
        complaintRefNo: data.complaintReferenceNo,
      }),
      priority: data.complaintPriority[0],
      imagePath: uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD,
    }
    // console.log(inputData);

    if (complaintData.isEdit) {
      const editData = {
        id: complaintData.complaintId,
        userId: complaintData.customerId || userId,
      }
      dispatch({
        type: "EDIT_COMPLAINT", // Make sure this action exists in your saga/reducer
        payload: { ...inputData, ...editData },
      })
    } else {
      // Create new complaint
      const createData = {
        createdDate: dateToTimeStamp(),
      }
      dispatch({
        type: "RAISE_NEW_COMPLAINT",
        payload: { ...inputData, ...createData },
      })
    }
  })

  React.useEffect(() => {
    if (id && id !== "") {
      dispatch(resetListData(listPath.COMPLAINTS));
      goBackToHomePage();
    };
  }
    , [id]);

  return (
    <Stack spacing={6} className={styles.container} border="1px solid" borderColor={borderColor} borderRadius="sm">

      <BreadCrumbBox navPageList={breadCrumbList} currentPage={complaintData.isEdit ? "Edit" : "Register"} />
      <Heading size="xl" className={styles.header}>{complaintData.title ?? "Add Complaint"}</Heading>
      <DialogBox
        dialog_title="Unsaved Changes"
        dialog_body="Are you sure to discard the changes?"
        cancelButtonName='No'
        SaveButtonName='Discard'
        open={cancelFormModalOpen}
        onClose={handleCancelClose}
        saveButtonOnclick={() => {
          goBackToHomePage();
          handleCancelClose()
        }}
      />

      <form onSubmit={onSubmit}>
        <Stack spacing={6}>
          <GridRow>
            <GridCol>
              <SelectBox
                label={<span>Complaint Category <span style={{ color: 'red' }}>*</span></span>}
                invalid={!!errors.complaintType}
                errorText={errors.complaintType?.message}
                control={control}
                name="complaintType"
                items={serviceRequestCategories}
              />
            </GridCol>
            <GridCol>
              <SelectBox
                label={<span>Complaint Sub Category <span style={{ color: 'red' }}>*</span></span>}
                invalid={!!errors.complaintSubCategory}
                errorText={errors.complaintSubCategory?.message}
                control={control}
                name="complaintSubCategory"
                disabled={!selectedComplaintCategory || selectedComplaintCategory.length === 0}
                items={
                  selectedComplaintCategory
                    ? serviceRequestSubcategories[selectedComplaintCategory] || []
                    : []
                }
              />
            </GridCol>
            <GridCol>
              <SelectBox
                label={<span>Complaint Priority <span style={{ color: 'red' }}>*</span></span>}
                invalid={!!errors.complaintPriority}
                errorText={errors.complaintPriority?.message}
                control={control}
                name="complaintPriority"
                items={complaint_priorities_list}
              />
            </GridCol>


          </GridRow>
          <GridRow>
            <GridCol>
              <InputBox
                label={<>{"Title"} {redStar}</>}
                invalid={!!errors.title}
                errorText={errors.title?.message}
                register={{ ...register('title') }}
                value={complaintData.complaintTitle}
              />
            </GridCol>

            <GridCol>
              <InputBox
                label={<>{"Description"} {redStar}</>}
                invalid={!!errors.description}
                errorText={errors.description?.message}
                register={{ ...register('description') }}
                value={complaintData.complaintDescription}
              />
            </GridCol>

            <GridCol>
              <InputBox
                label="Complaint Ref. No."
                invalid={!!errors.complaintReferenceNo}
                errorText={errors.complaintReferenceNo?.message}
                register={{ ...register('complaintReferenceNo') }}
                value={complaintData.complaintReferenceNumber}
              />
            </GridCol>
          </GridRow>
          <GridRow>
            <GridCol>
              <ImageUploader
                path={uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD}
                isEdit={complaintData.isEdit}
              />
              {!complaintData.isEdit && imageError && (
                <Text color="red.400" fontWeight="semibold" pt={2} fontSize="xs">{imageError}</Text>
              )}
            </GridCol>
          </GridRow>

          {/* Submit Button */}
          <Flex justify="flex-end" gap={4}>
            <ButtonBox name="Cancel" variant="plain" onClick={handleFormCancel} />
            <ButtonBox type="submit" name={complaintData.isEdit ? "Update" : "Submit"} />
          </Flex>
        </Stack>
      </form>

    </Stack>
  )
}

export default ComplaintAdd
